import React, { useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { STATUS_LABELS } from './constants'
import { PurchaseOrder, PurchaseOrderStatus } from './types'
import OrderCard from './OrderCard'
import ReceiveOrderConfirmation from './ReceiveOrderConfirmation'
import { KanbanCardSlot, KanbanColBody, KanbanColCount, KanbanColHeader, KanbanColTitle, KanbanColumn, KanbanWrapper } from './style'

const DRAG_TYPE = 'purchase-order-card'

const COLUMNS: PurchaseOrderStatus[] = ['EM_COTACAO', 'APROVADO', 'PEDIDO_EMITIDO', 'RECEBIDO']

interface DragItem {
  id: number
  status: PurchaseOrderStatus
}

interface DraggableCardProps {
  order: PurchaseOrder
  disabled: boolean
}

const DraggableCard: React.FC<DraggableCardProps> = ({ order, disabled }) => {
  // Recebido é status final: uma vez recebida, a OC já deu entrada em
  // estoque e não pode mais ser movida (ver bloqueio irreversível no backend).
  const isLocked = order.status === 'RECEBIDO'
  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: DRAG_TYPE,
    item: { id: order.id, status: order.status },
    canDrag: !disabled && !isLocked,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  return (
    <KanbanCardSlot ref={drag} $locked={isLocked} style={{ opacity: isDragging ? 0.4 : undefined }}>
      <OrderCard order={order} />
    </KanbanCardSlot>
  )
}

interface ColumnProps {
  status: PurchaseOrderStatus
  orders: PurchaseOrder[]
  updating: boolean
  onDropCard: (orderId: number, fromStatus: PurchaseOrderStatus, toStatus: PurchaseOrderStatus) => void
}

const Column: React.FC<ColumnProps> = ({ status, orders, updating, onDropCard }) => {
  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: DRAG_TYPE,
    drop: (item) => {
      if (item.status !== status) onDropCard(item.id, item.status, status)
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  })

  return (
    <KanbanColumn ref={drop} $isOver={isOver}>
      <KanbanColHeader>
        <KanbanColTitle>{STATUS_LABELS[status]}</KanbanColTitle>
        <KanbanColCount>{orders.length}</KanbanColCount>
      </KanbanColHeader>
      <KanbanColBody>
        {orders.map((order) => (
          <DraggableCard key={order.id} order={order} disabled={updating} />
        ))}
      </KanbanColBody>
    </KanbanColumn>
  )
}

interface KanbanViewProps {
  orders: PurchaseOrder[]
  onStatusChange: (orderId: number, status: PurchaseOrderStatus) => Promise<void>
}

interface PendingReceipt {
  orderId: number
  code: string
  title: string
}

const KanbanView: React.FC<KanbanViewProps> = ({ orders, onStatusChange }) => {
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const [pendingReceipt, setPendingReceipt] = useState<PendingReceipt | null>(null)

  const runStatusChange = async (orderId: number, toStatus: PurchaseOrderStatus) => {
    setUpdatingId(orderId)
    try {
      await onStatusChange(orderId, toStatus)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleDrop = (orderId: number, fromStatus: PurchaseOrderStatus, toStatus: PurchaseOrderStatus) => {
    if (fromStatus === toStatus || updatingId !== null) return

    if (toStatus === 'RECEBIDO') {
      const order = orders.find((o) => o.id === orderId)
      setPendingReceipt({ orderId, code: order?.code ?? '', title: order?.title ?? '' })
      return
    }

    runStatusChange(orderId, toStatus)
  }

  const confirmReceipt = () => {
    if (!pendingReceipt) return
    runStatusChange(pendingReceipt.orderId, 'RECEBIDO')
    setPendingReceipt(null)
  }

  return (
    <KanbanWrapper>
      {COLUMNS.map((status) => (
        <Column
          key={status}
          status={status}
          orders={orders.filter((order) => order.status === status)}
          updating={updatingId !== null}
          onDropCard={handleDrop}
        />
      ))}

      <ReceiveOrderConfirmation
        isOpen={pendingReceipt !== null}
        code={pendingReceipt?.code}
        title={pendingReceipt?.title}
        onCancel={() => setPendingReceipt(null)}
        onConfirm={confirmReceipt}
      />
    </KanbanWrapper>
  )
}

export default KanbanView
